import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar as ProSidebar, Menu, MenuItem } from 'react-pro-sidebar';
import nominationReviewIcon from '../../../assets/nomination_review.svg';

const ExternalSidebar: React.FC = () => {
  const location = useLocation();
  const [isCompactViewport, setIsCompactViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setIsCompactViewport(event.matches);
      setCollapsed(event.matches);
    };

    mediaQuery.addEventListener('change', handleBreakpointChange);
    return () => mediaQuery.removeEventListener('change', handleBreakpointChange);
  }, []);

  useEffect(() => {
    if (!isCompactViewport || collapsed) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCollapsed(true);
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [collapsed, isCompactViewport]);

  const isFeedbackActive = location.pathname.startsWith('/external-360-feedback');

  const menuItemStyles = {
    button: ({ active }: { active: boolean }) => ({
      backgroundColor: active ? '#5F279E' : 'transparent',
      color: active ? '#ffffff' : '#000000',
      padding: collapsed ? '0.5rem 0.25rem' : '1.25rem 1rem',
      margin: collapsed ? '0.375rem 0.625rem' : '0.375rem 1rem',
      borderRadius: '0.5rem',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      border: 'none',
      fontSize: '0.9rem',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: collapsed ? 'center' : 'flex-start',
      '&:hover': {
        backgroundColor: active ? '#7a32c4' : '#f5f2fc',
      },
    }),
    icon: ({ active }: { active: boolean }) => ({
      color: active ? '#ffffff' : '#000000',
      marginRight: collapsed ? '0' : '0.875rem',
      width: '35px',
      minWidth: '35px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    label: {
      display: collapsed ? 'none' : 'block',
      flexGrow: collapsed ? 0 : 1,
      fontWeight: 500,
      fontSize: '0.9rem',
      lineHeight: '1.25rem',
    },
  };

  return (
    <>
      {isCompactViewport && !collapsed && (
        <button
          type="button"
          className="fixed inset-0 z-0 cursor-default bg-black/30"
          onClick={() => setCollapsed(true)}
          aria-label="Close external navigation"
        />
      )}
      <style>{`
        .external-sidebar-active .external-sidebar-icon {
          filter: brightness(0) invert(1);
        }
        .external-sidebar-icon {
          transition: filter 0.2s ease;
        }
      `}</style>
      <ProSidebar
        collapsed={collapsed}
        width="280px"
        collapsedWidth="80px"
        backgroundColor="#ffffff"
        rootStyles={{
          border: 'none',
          borderRight: '1px solid #e2e8f0',
          height: '100dvh',
          position: 'relative',
          zIndex: 10,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div className="flex h-full flex-col">
          <div className="p-2 lg:p-4">
            <button
              type="button"
              onClick={() => setCollapsed((current) => !current)}
              aria-label={collapsed ? 'Expand external navigation' : 'Collapse external navigation'}
              className="flex w-full items-center justify-center rounded-xl border-[1.5px] border-purple-200 bg-white p-2 text-purple-800 shadow-sm transition hover:border-purple-400 hover:bg-purple-50 lg:w-auto lg:p-3"
            >
              {collapsed ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6l12 12M6 18L18 6" />
                </svg>
              )}
            </button>
          </div>

          <Menu className="py-2" menuItemStyles={menuItemStyles}>
            <MenuItem
              active={isFeedbackActive}
              className={isFeedbackActive ? 'external-sidebar-active' : undefined}
              icon={<img src={nominationReviewIcon} alt="360 Feedback" className="external-sidebar-icon h-8 w-8" />}
              title={collapsed ? '360 Feedback' : undefined}
              aria-label={collapsed ? '360 Feedback' : undefined}
              component={<Link to={{ pathname: '/external-360-feedback', search: location.search }} />}
            >
              {!collapsed && '360 Feedback'}
            </MenuItem>
          </Menu>
        </div>
      </ProSidebar>
    </>
  );
};

export default ExternalSidebar;